import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { PieChart, AlertTriangle } from "lucide-react";

function money(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const BAR_COLORS = ["#FF9500", "#34D399", "#60A5FA", "#F472B6", "#FBBF24"];

/**
 * "Where the budget's going" strip — shows top 3 cost-code buckets for a job
 * with $ + %-of-spend, and flags any bucket >40% as the "budget hog".
 * Silent when the job has zero spend so Capture stays clean.
 */
export default function CostBreakdownStrip({ jobId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    let mounted = true;
    api
      .get(`/jobs/${jobId}/cost-breakdown`)
      .then(({ data: res }) => {
        if (mounted) setData(res);
      })
      .catch((err) => {
        devLog.warn("cost-breakdown load failed", err);
      });
    return () => {
      mounted = false;
    };
  }, [jobId]);

  if (!data || data.total_spend <= 0 || data.rows.length === 0) return null;

  const top = data.rows.slice(0, 3);

  return (
    <div className="pt-3 border-t border-white/5" data-testid="job-cost-breakdown">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading inline-flex items-center gap-1">
          <PieChart className="w-3 h-3" /> Where the budget&apos;s going
        </div>
        {data.hog && (
          <div
            className="text-[10px] text-[#FBBF24] inline-flex items-center gap-1"
            data-testid="cost-hog-flag"
          >
            <AlertTriangle className="w-3 h-3" />
            {data.hog.label} · {data.hog.pct_of_spend.toFixed(0)}%
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        {top.map((row, idx) => (
          <div
            key={row.label}
            className="flex items-center gap-2"
            data-testid={`cost-row-${row.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div
              className="w-1.5 h-6 rounded-full shrink-0"
              style={{ background: BAR_COLORS[idx % BAR_COLORS.length] }}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="truncate font-semibold text-white/90">{row.label}</span>
                <span className="tabular-nums text-white/80 font-heading font-extrabold">
                  {money(row.spent)}
                </span>
              </div>
              <div className="mt-0.5 h-1 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full transition-[width] duration-500"
                  style={{
                    width: `${Math.min(100, row.pct_of_spend)}%`,
                    background: BAR_COLORS[idx % BAR_COLORS.length],
                  }}
                />
              </div>
              <div className="text-[10px] text-white/40 mt-0.5 tabular-nums">
                {row.pct_of_spend.toFixed(0)}% of spend · {row.count}{" "}
                {row.count === 1 ? "entry" : "entries"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
